import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, 
  LogOut,
  Users,
  FileText,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Activity,
  Clock,
  Settings,
  ChevronRight,
  Loader2,
  LayoutDashboard,
  Briefcase
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import type { Agent, Case, Debtor, ExceptionRequest, DashboardStats } from '../types';

interface DashboardPageProps {
  user: Agent;
  onLogout: () => void;
}

export default function DashboardPage({ user, onLogout }: DashboardPageProps) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentCases, setRecentCases] = useState<Case[]>([]);
  const [pendingRequests, setPendingRequests] = useState<ExceptionRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Load cases for statistics
      const cases = await serenityClient.queryData<Case>(
        'SELECT * FROM Cases ORDER BY created_at DESC'
      );

      // Load debtors for segment stats
      const debtors = await serenityClient.queryData<Debtor>(
        'SELECT debtor_id, segment FROM Debtors'
      );

      // Load pending requests
      const requests = await serenityClient.queryData<ExceptionRequest>(
        `SELECT * FROM ExceptionRequests WHERE status IN ('pending', 'under_review') ORDER BY created_at DESC LIMIT 5`
      );
      setPendingRequests(requests);

      // Set recent cases
      setRecentCases(cases.slice(0, 5));

      // Calculate statistics
      const activeCases = cases.filter(c => 
        !['paid_in_full', 'settled', 'write_off'].includes(c.status)
      );
      const totalDebt = cases.reduce((sum, c) => sum + c.total_amount, 0);
      const collectedAmount = cases.reduce((sum, c) => sum + (c.amount_paid || 0), 0);

      const casesPerSegment = {
        high: debtors.filter(d => d.segment === 'high').length,
        medium: debtors.filter(d => d.segment === 'medium').length,
        low: debtors.filter(d => d.segment === 'low').length,
      };

      const casesPerStatus: Record<string, number> = {};
      cases.forEach(c => {
        casesPerStatus[c.status] = (casesPerStatus[c.status] || 0) + 1;
      });

      setStats({
        totalCases: cases.length,
        activeCases: activeCases.length,
        totalDebt,
        collectedAmount,
        pendingRequests: requests.length,
        casesPerSegment,
        casesPerStatus: casesPerStatus as DashboardStats['casesPerStatus'],
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
      case 'in_contact':
        return 'bg-blue-100 text-blue-800';
      case 'negotiating':
      case 'arrangement':
        return 'bg-yellow-100 text-yellow-800';
      case 'paying':
        return 'bg-green-100 text-green-800';
      case 'broken_arrangement':
      case 'pre_legal':
      case 'legal':
        return 'bg-red-100 text-red-800';
      case 'paid_in_full':
      case 'settled':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', active: true },
    { path: '/cases', icon: Briefcase, label: 'Cases' },
    { path: '/debtors', icon: Users, label: 'Debtors' },
    { path: '/requests', icon: AlertTriangle, label: 'Requests' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col">
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-semibold">DFS Collections</h1>
              <p className="text-xs text-slate-400">Agent Portal</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    item.active 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-sm font-medium">
                {user.first_name[0]}{user.last_name[0]}
              </div>
              <div>
                <p className="text-sm font-medium">{user.first_name}</p>
                <p className="text-xs text-slate-400">{user.role}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-500">Welcome back, {user.first_name}</p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : stats ? (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-gray-500">Total Cases</span>
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalCases}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {stats.activeCases} active
                  </p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-gray-500">Total Debt</span>
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-red-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(stats.totalDebt)}</p>
                  <p className="text-sm text-gray-500 mt-1">Portfolio value</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-gray-500">Collected</span>
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <TrendingDown className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(stats.collectedAmount)}</p>
                  <p className="text-sm text-green-600 mt-1">
                    {stats.totalDebt > 0 ? ((stats.collectedAmount / stats.totalDebt) * 100).toFixed(1) : 0}% recovery
                  </p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-gray-500">Pending Requests</span>
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{stats.pendingRequests}</p>
                  <Link to="/requests" className="text-sm text-blue-600 hover:underline mt-1 inline-block">
                    Review requests →
                  </Link>
                </div>
              </div>

              {/* Segment Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Debtors by Segment</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">High Priority</span>
                        <span className="text-sm font-medium text-gray-900">{stats.casesPerSegment.high}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${(stats.casesPerSegment.high / (stats.casesPerSegment.high + stats.casesPerSegment.medium + stats.casesPerSegment.low)) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">Medium Priority</span>
                        <span className="text-sm font-medium text-gray-900">{stats.casesPerSegment.medium}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full" 
                          style={{ width: `${(stats.casesPerSegment.medium / (stats.casesPerSegment.high + stats.casesPerSegment.medium + stats.casesPerSegment.low)) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">Low Priority</span>
                        <span className="text-sm font-medium text-gray-900">{stats.casesPerSegment.low}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full" 
                          style={{ width: `${(stats.casesPerSegment.low / (stats.casesPerSegment.high + stats.casesPerSegment.medium + stats.casesPerSegment.low)) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Cases */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Cases</h3>
                    <Link to="/cases" className="text-sm text-blue-600 hover:underline">
                      View all
                    </Link>
                  </div>
                  <div className="space-y-3">
                    {recentCases.map((caseItem) => (
                      <Link
                        key={caseItem.case_id}
                        to={`/case/${caseItem.case_id}`}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{caseItem.case_id}</p>
                          <p className="text-sm text-gray-500">{caseItem.origin} · {caseItem.cedent}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(caseItem.status)}`}>
                            {getStatusLabel(caseItem.status)}
                          </span>
                          <span className="font-medium text-gray-900">
                            {formatCurrency(caseItem.total_amount - (caseItem.amount_paid || 0))}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Pending Requests */}
              {pendingRequests.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Pending Exception Requests</h3>
                    <Link to="/requests" className="text-sm text-blue-600 hover:underline">
                      View all
                    </Link>
                  </div>
                  <div className="space-y-3">
                    {pendingRequests.map((req) => (
                      <div
                        key={req.request_id}
                        className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200"
                      >
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-5 h-5 text-orange-600" />
                          <div>
                            <p className="font-medium text-gray-900">{req.case_id}</p>
                            <p className="text-sm text-gray-500">{req.request_type} - {req.reason}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Clock className="w-4 h-4 text-orange-600" />
                          <span className="text-sm text-orange-700">Pending review</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-500">Failed to load dashboard data</div>
          )}
        </div>
      </main>
    </div>
  );
}
